//
// replicator.ts
//
// Copyright 2024-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

import { type Database, type NoSchema, ReadOnly, type SchemaLike } from "./database";
import { DocumentMeta, meta, type CBLDictLike, type CBLDocument } from "./document";
import * as repl from "../replicator/replicator";
import { DocumentFlags } from "../replicator/types";
import { assert } from "@/util/assert";
import * as blob from "@/blob/blob";
import type * as logtape from "@logtape/logtape";
import type { CBLDictionary, DocID, RevID, Sequence } from "./types";
import type { Collection } from "./collection";
import { greaterGeneration, RevFlagConflicted, type LocalRevision, type StoredRevision } from "./internals";
import { sha1 } from "sha.js";
import type { ListenerToken } from "@/util/listener";
import type { JSONObject } from "@/util/json_types";
import { ImportBlobs } from "@/blob/doc_blobs";
import { Timer } from "@/util/timer";
import { WebSocketError } from "@/blip/blip";


const kRetryDelaySec    =      1;    // Initial retry delay; doubles each time
const kMaxRetryDelaySec = 5 * 60;    // Maximum retry delay


const kDefaultHeartbeat = 5 * 60 * 1000;


/** Configuration for {@link Replicator}.
 *  @interface
 *  @property database  The Database to sync.
 *  @property url  Server URL to connect to, with scheme "wss:" or "ws:", and database name
 *                 as first path component.
 *  @property continuous  Default value of `continuous` for all collections.
 *  @property heartbeat  Interval in ms to send pings if there's been no traffic [default 10 min]
 *  @property collections  Keys are names of collections to sync;
 *                         values are per-collection configuration. */
export interface ReplicatorConfig<S extends SchemaLike<S> = NoSchema> {
    database    : Database<S>,
    url         : string,
    credentials?: repl.Credentials,
    continuous? : boolean,
    heartbeat?  : number,
    collections : { [K in keyof S]?: ReplicatorCollectionConfig<S[K]> },
}


/** Per-collection Replicator configuration.
 *  @property push  Configuration for pushing. If not present, replicator will not push.
 *  @property pull  Configuration for pulling. If not present, replicator will not pull.
 *  @property documentIDs  If set, only these documents will be pushed or pulled.
 *  @property resetCheckpoint  If true, the replicator will ignore its current state and compare
 *                             all document revision IDs with the server.  */
export interface ReplicatorCollectionConfig<D extends CBLDictLike<D> = CBLDictionary> {
    push?               : PushConfig<D>,
    pull?               : PullConfig<D>,
    documentIDs?        : readonly DocID[],
    resetCheckpoint?    : boolean,
}

/** Configuration parameters for pushing changes to the remote collection.
 *  @property continuous    If true, stay connected indefinitely.
 *  @property filter        Callback that can skip revisions one at a time. */
export interface PushConfig<D extends CBLDictLike<D> = CBLDictionary> {
    continuous?         : boolean
    filter?             : repl.ReplicationFilter<D>;
}

/** Configuration parameters for pulling changes from a remote collection.
 *  @property continuous    If true, stay connected indefinitely.
 *  @property channels      Optional set of Sync Gateway channels, for server-side filtering.
 *  @property enableAutoPurge   If true, automatically purges documents when the user loses access
 *                              through channel revocation on Sync Gateway. Defaults to true.
 *  @property filter        Callback that can skip individual revisions.
 *  @property conflictResolver  Callback that resolves conflicts between local and server docs.
 *                              If not given, a default resolver is used that chooses the one
 *                              with the higher revision ID (Most Writes Wins.) */
export interface PullConfig<D extends CBLDictLike<D> = CBLDictionary> {
    continuous?         : boolean
    channels?           : readonly string[],
    enableAutoPurge?    : boolean,
    filter?             : repl.ReplicationFilter<D>,
    conflictResolver?   : repl.PullConflictResolver<D>,
}


/** Information about a document that's been pushed or pulled by a replicator.
 *  @see {@link Replicator.onDocuments} */
export interface DocumentEnded {
    docID       : DocID,
    deleted?    : boolean,
    lostAccess? : repl.LostAccess,
    error?      : Error,
}


/** Syncs one or more {@link Collection}s with their remote counterparts. */
export class Replicator<S extends SchemaLike<S> = NoSchema> implements repl.CheckpointerDelegate {

    constructor(private config: ReplicatorConfig<S>) {
        this.database = config.database;
        this.logger = repl.SyncLogger.with({url: config.url});
        const ids = Object.keys(this.config.collections);
        assert(ids.length > 0, "At least one collection must be replicated");
        for (const id of ids)
            void(this.database.getCollection(id));  // just verify it exists
    }

    /** The local database being replicated. */
    readonly database: Database<S>;

    /** Callback that notifies when {@link status} changes. */
    onStatusChange? : (status: repl.Status) => void;

    /** Callback that notifies when documents have been pushed or pulled.
     *
     * Note: To receive lost-access (revocation) notifications from the pull replicator
     * when auto-purge is disabled, this callback must be registered *before*
     * starting the pull replicator. */
    onDocuments? : (collection: Collection, direction: 'push' | 'pull', documents: DocumentEnded[]) => void;


    /** Current replication status & progress. */
    get status(): repl.Status {return this.#replicator?.status ?? this.#lastStatus;}


    /** Runs the replicator.
     *
     * This is an async operation. The returned Promise resolves when the replication completes.
     * A continuous replication usually _never_ completes, unless it encounters a fatal error or
     * you stop it, so you may not want to `await` it. */
    async run() {
        assert(!this.#replicator, "Replicator is already running");
        this.#stopped = false;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let redactedCfg = {...this.config} as Record<string,any>;
        if (this.config.credentials)
            redactedCfg.credentials = {username: this.config.credentials.username};
        this.logger.info `Starting replicator with configuration: ${redactedCfg}`;

        // Construct actual replicator config:
        let replCfg: repl.ReplicatorConfig = {
            url: this.config.url,
            credentials: this.config.credentials,
            heartbeatMS: this.config.heartbeat ?? kDefaultHeartbeat,
            collections: {},
        };
        let continuous = this.config.continuous;
        const erasedCollections = this.config.collections as ReplicatorConfig['collections'];
        for (let id of Object.keys(erasedCollections)) {
            const collCfg = erasedCollections[id]!;
            const collection = this.database.getCollection(id);
            const clientID = await this.getCheckpointID(collection);
            const reset = collCfg.resetCheckpoint;
            const initialCheckpoint = reset ? undefined : await collection.getCheckpoint(clientID);

            let replCollCfg: repl.CollectionConfig = {
                checkpoint: {clientID, initialCheckpoint, reset, delegate: this}
            };
            let pusherDelegate;
            if (collCfg.push) {
                const pusherCfg = {...collCfg.push, documentIDs: collCfg.documentIDs};
                pusherDelegate = new PusherDelegateImpl(this as Replicator, collection, pusherCfg);
                replCollCfg.push = {...pusherCfg, delegate: pusherDelegate};
                if (replCollCfg.push.continuous === undefined)
                    replCollCfg.push.continuous = this.config.continuous;
                else if (replCollCfg.push.continuous)
                    continuous = true;
            }
            if (collCfg.pull) {
                const pullerCfg = {...collCfg.pull, documentIDs: collCfg.documentIDs};
                const startedEmpty = await collection.count('includeDeleted') === 0;
                const delegate = new PullerDelegateImpl(this as Replicator, collection, pullerCfg,
                                                        startedEmpty, pusherDelegate);
                replCollCfg.pull = {...pullerCfg, delegate: delegate, activeOnly: startedEmpty};
                if (replCollCfg.pull.continuous === undefined)
                    replCollCfg.pull.continuous = this.config.continuous;
                else if (replCollCfg.pull.continuous)
                    continuous = true;
            }
            replCfg.collections[id as repl.CollectionID] = replCollCfg;
        }

        // Now run the Replicator:
        let retry = true;
        let failureCount = 0;
        while(retry && !this.#stopped) {
            retry = false;
            // Create the underlying repl.Replicator:
            this.#replicator = new repl.Replicator(replCfg);
            this.#replicator.onStatusChange = (status) => {
                if (status.status === 'busy' || status.status === 'idle')
                    failureCount = 0;   // On successful connect, reset retry delay back to 1s
                if (status.status !== 'stopped')
                    this.onStatusChange?.(status);
            };

            // Run it, catching any error:
            let error = undefined;
            try {
                await this.#replicator.run();
                this.#lastStatus = this.#replicator.status;
            } catch(x) {
                error = x as Error;
            }
            this.#lastStatus = this.#replicator.status;
            this.#lastStatus.error = error;
            this.#replicator = undefined;

            if (error && continuous && !this.#stopped && !isFatalError(error)) {
                // Non-fatal error in continuous replication; wait and retry. Delay is initially
                // 1sec, and doubles every time, up to a max of 5min. If the replicator manages
                // to connect, the delay drops back to the initial one.
                const delay = Math.min(kRetryDelaySec << failureCount++, kMaxRetryDelaySec);
                this.logger.warn `Will retry continuous replication in ${delay} sec, after error ${error}`;
                this.#lastStatus.status = 'offline';
                this.onStatusChange?.(this.#lastStatus);
                this.#abortSleep = new AbortController();
                retry = await Timer.sleep(delay * 1000, this.#abortSleep);
                this.#abortSleep = undefined;
                if (!retry) {
                    this.#lastStatus.status = 'stopped';
                    this.onStatusChange?.(this.#lastStatus);
                }
            } else {
                this.onStatusChange?.(this.#lastStatus);
                if (error)
                    throw error;
            }
        }

        this.logger.info `FINISHED! ${JSON.stringify(this.status)}`;
    }


    /** Stops the replicator. The current {@link run} operation's Promise will resolve with
     *  an error as soon as possible.
     *
     *  Does nothing if `run` is not active. */
    stop() {
        this.#stopped = true;
        this.#replicator?.stop();
        this.#abortSleep?.abort();
        this.#abortSleep = undefined;
    }


    /** Returns the checkpoint ID that will be used for a collection. @internal */
    async getCheckpointID(collection: Collection): Promise<string> {
        const cfg = this.config.collections[collection.name as keyof S]!;
        const hashed = [
            await collection.getUUID(),             // Collection ID
            this.config.url,                        // Remote database URL
            cfg.documentIDs?.toSorted() ?? null,    // Documents filter
            cfg.pull?.channels?.toSorted() ?? null, // Channel filter
            !!cfg.pull?.filter,                     // Custom pull filter present (boolean)
            !!cfg.push?.filter,                     // Custom push filter present (boolean)
        ];
        return new sha1().update(JSON.stringify(hashed)).digest('base64');
    }


    //-------- Checkpointer delegate implementation

    /** Saves a checkpoint to the local database. @internal */
    async saveCheckpoint(id: repl.CollectionID, clientID: string, checkpoint: repl.Checkpoint): Promise<void> {
        await this.database.getCollection(id).setCheckpoint(clientID, checkpoint);
    }


    readonly logger : logtape.Logger;

    #replicator?    : repl.Replicator;              // Actual replicator, while it's running
    #stopped        = false;                        // Flag set by stop()
    #abortSleep?    : AbortController;              // Aborts the retry delay
    #lastStatus     : repl.Status = {};             // Final status of the Replicator
}


function isFatalError(error: Error): boolean {
    if (error instanceof repl.ReplicatorError && error.code !== undefined) {
        switch (error.code) {
            case 408: return false; // Request Timeout
            case 500: return false; // Server Error
            case 502: return false; // Bad Gateway
            case 504: return false; // Gateway Timeout
            default:  return true;
        }
    } else if (error instanceof WebSocketError) {
        switch (error.code) {
            case 1000: return false; // Normal close (but this wouldn't be in an error)
            case 1005: return false; // No status received (BLIP throws this if it hadn't connected)
            case 1006: return false; // Abnormal closure (BLIP throws this if connection lost)
            case 1012: return false; // Server restarting
            case 1013: return false; // Server temporarily unavailable
            default:   return true;
        }
    } else if (error.name === 'TypeError') {
        // fetch() throws TypeError if there is a network error, and we need to retry those.
        // Unfortunately it throws the same error on various fatal conditions like invalid URLs.
        // And of course all kinds of other things throw TypeError...
        return false;
    } else {
        return true;
    }
}


class DelegateImpl {
    constructor(readonly replicator: Replicator, readonly collection: Collection, dir: string) {
        this.logger = replicator.logger.getChild([dir, collection.name]);
    }

    protected readonly logger: logtape.Logger;
}


//-------- PUSHER DELEGATE:


class PusherDelegateImpl extends DelegateImpl implements repl.PusherDelegate {

    constructor(replicator: Replicator, collection: Collection,
                readonly config: repl.PusherConfig)
    {
        super(replicator, collection, 'push');
        if (config.documentIDs)
            this.#docIDs = new Set(config.documentIDs);
    }


    async getChanges(since: repl.LocalSequence | undefined,
                     limit: number): Promise<repl.ChangeSet>
    {
        const collection = this.collection;
        const changes = await this.getDocsSinceSequence(since, limit);
        if (this.replicator.status.status === 'stopped') {
            return {changes: [], since: since};
        } else if (changes.changes.length > 0 || !this.config.continuous) {
            // Got changes; or in a non-continuous replication return the empty list as a signal
            // that there aren't any more:
            return changes;
        } else if (!this.#caughtUp) {
            // Pusher is in continuous mode and there are no more changes.
            // The first time this happens, return the empty list as a signal that it's caught up:
            this.#caughtUp = true;
            return changes;
        } else {
            // After that, use a 'collectionChanged' event listener to wait for changes to return:
            assert(!this.#asyncGetChanges);
            this.#asyncGetChanges = Promise.withResolvers<repl.ChangeSet>();
            let listener: ListenerToken;
            const onChanges = () => {
                listener.remove(); // one-shot
                const resolve = this.#asyncGetChanges?.resolve;
                if (resolve) {
                    this.logger.debug `Getting new changes...`;
                    this.#asyncGetChanges = undefined;
                    resolve(this.getDocsSinceSequence(since, limit));
                }
            };
            listener = collection.addChangeListener(onChanges);
            this.logger.info `Watching for changes...`;
            return this.#asyncGetChanges.promise;
        }
    }


    /** Returns an updated `serverRev` of a document, which hasn't been saved back to the db yet,
     *  or `undefined` if none. This reflects a recently pushed revision. */
    unsavedServerRev(docID: DocID): RevID | undefined {
        return this.#pushedRevs?.get(docID) ?? this.#updatingServerRevs?.get(docID);
    }


    private async getDocsSinceSequence(since: repl.LocalSequence | undefined,
                                       limit: number) : Promise<repl.ChangeSet>
    {
        let changes: LocalRevision[] = [];
        let lastSequence = since;
        let atEnd;
        // Ask for sequences, filter them, and add 'em to `changes` until we reach `limit`:
        do {
            const sinceFilter = (rev: StoredRevision) =>
                (!rev.flags || !(rev.flags & RevFlagConflicted)) &&     // no conflicts
                (rev.rev !== rev.serverRev);                            // no unchanged pulled docs
            const batch = await this.collection.getDocsSinceSequence(lastSequence, limit, sinceFilter);
            if (batch.length === 0)
                break;      // No more sequences in the collection
            atEnd = (batch.length < limit - changes.length);
            lastSequence = batch.at(-1)?.seq;

            if (this.#docIDs || this.config.filter) {
                const filter = this.config.filter;
                for (const rev of batch) {
                    if (this.#docIDs && !this.#docIDs.has(rev.id))
                        continue;
                    if (filter) {
                        const m = new DocumentMeta(this.collection, rev.id, rev.body, rev.rev, rev.seq);
                        const flags = rev.deleted ? DocumentFlags.deleted : DocumentFlags.none;
                        if (!filter(m.body, flags))
                            continue;
                    }
                    changes.push(rev);
                }
            } else {
                changes.push(...batch);
            }
        } while (changes.length < limit && !atEnd);

        if (changes.length > 0 && (this.#pushedRevs || this.#updatingServerRevs)) {
            // Make sure their `serverRev`s are up to date, since those are updated asynchronously:
            for (const rev of changes) {
                const serverRev = this.unsavedServerRev(rev.id);
                if (greaterGeneration(serverRev, rev.serverRev))
                    rev.serverRev = serverRev;
            }
        }
        return {changes, since, lastSequence};
    }


    async getBlob(digest: string): Promise<Uint8Array | undefined> {
        return await this.collection.database.blobStore.getBlob(digest);
    }


    pushedRev(rev: repl.PushRevision, error?: Error): void {
        if (!error) {
            // After pushing, update the document's `serverRev` to the pushed revID.
            // Do this asynchonously, in batches, to avoid a txn per doc pushed.
            let pushedRevs = this.#pushedRevs;
            if (!pushedRevs) {
                pushedRevs = new Map();
                this.#pushedRevs = pushedRevs;
            }
            pushedRevs.set(rev.id, rev.rev);
            this.updateServerRevsNow();
        }
        this.notifyDocEnded(rev, error);
    }

    private updateServerRevsNow() {
        let pushedRevs = this.#pushedRevs;
        if (pushedRevs && !this.#updatingServerRevs) {
            this.logger.debug `Updating serverRev property of ${pushedRevs.size} pushed docs`;
            this.#pushedRevs = undefined;
            this.#updatingServerRevs = pushedRevs;

            this.collection.updateServerRevs(pushedRevs).
                catch( error => this.logger.error `Error updating local serverRevs: ${error}`).
                finally( () => {
                    this.#updatingServerRevs = undefined;
                    // Update some more, if any have been added:
                    if (this.#pushedRevs)
                        this.updateServerRevsNow();
                });
        }
    }

    private notifyDocEnded(rev: repl.PushRevision, error?: Error) {
        if (this.replicator.onDocuments) {
            this.replicator.onDocuments(this.collection, 'push', [ {
                docID:   rev.id,
                deleted: !!rev.deleted,
                error:   error,
            } ] );
        }
    }


    #docIDs?             : Set<DocID>;
    #caughtUp            = false;
    #asyncGetChanges?    : PromiseWithResolvers<repl.ChangeSet>;
    #pushedRevs?         : Map<DocID,RevID>;                    // docs whose serverRevIDs to update
    #updatingServerRevs? : Map<DocID,RevID>;                    // docs being updated right now
}


//-------- PULLER DELEGATE:


class PullerDelegateImpl extends DelegateImpl implements repl.PullerDelegate {

    constructor(replicator: Replicator, collection: Collection,
                readonly config: repl.PullerConfig,
                public startedEmpty: boolean,
                private readonly pusherDelegate?: PusherDelegateImpl)
    {
        super(replicator, collection, 'pull');
        this.blobLoader = this.collection.database.blobLoader;
    }

    readonly blobLoader: blob.BlobLoader;


    async wantRevs(revs: repl.RemoteRevisionInfo[], caughtUp: boolean): Promise<void> {
        if (caughtUp)
            this.startedEmpty = false;
        else if (this.startedEmpty)
            return;

        let toBePurged: DocumentEnded[] = [];

        await this.collection.inTransaction(ReadOnly, async (coll) => {
            for (const rev of revs) {
                if (rev.lostAccess && !rev.deleted) {
                    rev.skip = true;
                    toBePurged.push({docID: rev.id, lostAccess: rev.lostAccess});
                } else if (!rev.skip) {
                    const unsavedServerRev = this.pusherDelegate?.unsavedServerRev(rev.id);
                    if (unsavedServerRev === rev.rev || greaterGeneration(unsavedServerRev, rev.rev)) {
                        // I have already pushed this revision, or a newer one
                        rev.skip = true;
                    } else {
                        const knownRevs = await coll.getAncestorRevs(rev.id, rev.rev);
                        if (knownRevs)
                            rev.knownRevs = knownRevs;
                        else
                            rev.skip = true;
                    }
                }
            }
        });

        if (toBePurged.length > 0) {
            const filter = this.config.filter;
            if (filter) {
                toBePurged = toBePurged.filter( d => {
                    const m = new DocumentMeta(this.collection, d.docID, {}, undefined, undefined);
                    return filter(m.body, DocumentFlags.accessRemoved);
                });
            }
            if (toBePurged.length > 0) {
                if (this.config.enableAutoPurge !== false) {
                    await this.collection.purgeMultiple(toBePurged.map(d => d.docID));
                }
                this.replicator.onDocuments?.(this.collection, 'pull', toBePurged);
            }
        }
    }


    async saveRevs(revs: repl.RemoteRevision[]): Promise<boolean> {
        const collection = this.collection;

        // Apply the pull filter first:
        const filter = this.config.filter;
        if (filter) {
            revs = revs.filter( rev => {
                const m = new DocumentMeta(collection, rev.id, rev.body, rev.rev, 0 as Sequence);
                let flags = DocumentFlags.none;
                if (rev.deleted) flags |= DocumentFlags.deleted;
                if (rev.lostAccess) flags |= DocumentFlags.accessRemoved;
                return filter(m.body, flags);
            });
        }
        if (revs.length === 0)
            return true;

        this.logger.debug `Saving ${revs.length} documents...`;
        const now = Date.now();
        const {lastSequence, conflicts} = await collection.putRemoteRevisions(revs, this.startedEmpty);
        this.logger.info `Saved ${revs.length} documents, ${conflicts?.size ?? 0} conflicts, as sequences thru ${lastSequence}, in ${Date.now() - now}ms`;

        if (this.replicator.onDocuments) {
            if (conflicts)
                revs = revs.filter( rev => !conflicts.has(rev.id) );
            const docs = revs.map( rev => ({docID: rev.id, deleted: !!rev.deleted}) );
            this.replicator.onDocuments(collection, 'pull', docs);
        }

        if (conflicts)
            void this.resolveConflicts(conflicts);
        return true;
    }


    async missingBlobs(digests: Set<string>): Promise<string[] | undefined> {
        let missing: string[] | undefined;
        for (const digest of digests) {
            if (!await this.collection.database.blobStore.hasBlob(digest)) {
                if (!missing) missing = [];
                missing.push(digest);
            }
        }
        return missing;
    }


    async addBlob(digest: string, contents: Uint8Array): Promise<void> {
        const gotDigest = blob.NewBlob.computeDigest(contents);
        if (digest !== gotDigest)
            throw new repl.ReplicatorError(`Requested blob digest '${digest}' but the data's digest is '${gotDigest}'`, 400);
        await this.collection.database.blobStore.saveBlob(contents, digest);
    }


    hasOnDocumentsCallback(): boolean {
        return !!this.replicator.onDocuments;
    }


    private async resolveConflicts(conflictIDs: Set<DocID>) {
        const collection = this.collection;
        const resolver = this.config.conflictResolver ?? defaultConflictResolver;
        for (const docID of conflictIDs) {
            try {
                let rev: LocalRevision | undefined;
                let resolved!: JSONObject | null;
                do {
                    rev = await collection.getRevision(docID);
                    const conflictBody = rev?.conflict;
                    if (rev === undefined || conflictBody === undefined)
                        break;
                    let local = collection.revToDoc(rev) ?? null;
                    let remote: CBLDocument | null = null;
                    if (conflictBody !== null) {
                        const body = await collection.decryptConflict(conflictBody);
                        ImportBlobs(body, collection.database.blobLoader);
                        remote = new DocumentMeta(this.collection, rev.id, body,
                                                  rev.serverRev, 0 as Sequence).body;
                    }
                    const resolvedDoc = await resolver(local, remote);
                    resolved = resolvedDoc ? (resolvedDoc as JSONObject) : null;
                } while (!await collection.resolveConflict(docID, rev.rev, resolved));

                if (this.replicator.onDocuments) {
                    const doc = {docID: docID, deleted: (resolved === null)};
                    this.replicator.onDocuments(collection, 'pull', [doc]);
                }
            } catch (x) {
                this.logger.error `Exception resolving conflict in in doc ${docID}: ${x}`;
            }
        }
    }
}


// eslint-disable-next-line @typescript-eslint/require-await
async function defaultConflictResolver(local: CBLDocument | null,
                                       remote: CBLDocument | null): Promise<CBLDocument | null>
{
    if (!local || !remote)
        return null;        // A deletion always wins
    else if (greaterGeneration(meta(local).revisionID, meta(remote).revisionID))
        return local;
    else
        return remote;
}
